import clsx from "clsx";
import React, { useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import { ArrowContainer, Popover } from "react-tiny-popover";
import { useDevices } from "../../../hooks";
import { getElementsByClassName, getFirstElementByClassName, uniqueStr } from "../../../utils";
import { PopoverElementBacker } from "./GenericPopoverStyles";
export const Triggers = {
    HOVER: "hover",
    CLICK: "click",
};
const popoverClassName = "bcr-popover";
const initState = {
    open: false,
    timeoutRef: undefined,
};
const popoverClosedClass = "bcr-popover-closed";
function reducer(state, action) {
    switch (action.type) {
        case "setIsOpen":
            return { ...state, setIsOpen: action.payload };
        case "closePopover": {
            if (state.timeoutRef) {
                clearTimeout(state.timeoutRef);
            }
            if (!!state.setIsOpen) {
                state.setIsOpen(false);
            }
            return { ...state, open: false, timeoutRef: undefined };
        }
        case "toggleOpen": {
            const newOpenValue = !state.open;
            if (!!state.setIsOpen) {
                state.setIsOpen(newOpenValue);
            }
            return { ...state, open: newOpenValue };
        }
        case "setOpen": {
            const newOpenValue = action.payload;
            if (!!state.setIsOpen) {
                state.setIsOpen(newOpenValue);
            }
            return { ...state, open: newOpenValue };
        }
        case "setTimeoutRef": {
            return { ...state, timeoutRef: action.payload };
        }
        default:
            return state;
    }
}
const GenericPopover = ({ children, delay = 0, trigger = "hover", location = "top", align = "center", disabled = false, singleOpen = true, portal = document.body, arrowColor = "", showArrow = false, reposition, setIsOpen, element, }) => {
    const { isMobile } = useDevices();
    const [popoverId] = useState(`bcr-popover-${uniqueStr()}`);
    const popoverRef = useRef(null);
    const [elementWidth, setElementWidth] = useState(0);
    const [elementHeight, setElementHeight] = useState(0);
    const [finalTrigger, setFinalTrigger] = useState(trigger);
    const [state, dispatch] = useReducer(reducer, initState);
    function togglePopover(event) {
        const elements = getElementsByClassName(popoverClassName);
        if (!state.open && singleOpen && !!elements) {
            for (const element of Array.from(elements)) {
                if (!!element && !element.className.includes(popoverClosedClass)) {
                    try {
                        element.className = `${element.className} ${popoverClosedClass}`;
                    }
                    catch (e) {
                        console.error("Error removing popovers", e);
                    }
                }
            }
        }
        event.stopPropagation();
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!disabled) {
            if (!!state.timeoutRef) {
                clearTimeout(state.timeoutRef);
            }
            const tRef = setTimeout(() => {
                dispatch({ type: "toggleOpen" });
                dispatch({ type: "setTimeoutRef", payload: undefined });
            }, delay);
            dispatch({ type: "setTimeoutRef", payload: tRef });
        }
    }
    const closePopover = (event) => {
        event.stopPropagation();
        event.preventDefault();
        event.stopImmediatePropagation();
        dispatch({ type: "closePopover" });
    };
    const checkForOpen = (entries, observer) => {
        if (!!entries?.length) {
            const item = entries[0];
            if (item?.target?.clientHeight === 0) {
                item.target.className = item.target.className.replace(popoverClosedClass, "");
                dispatch({ type: "setOpen", payload: false });
            }
        }
    };
    useEffect(() => {
        if (!!element?.current) {
            setElementWidth(element.current.offsetWidth);
            setElementHeight(element.current.offsetHeight);
            if (finalTrigger === Triggers.HOVER) {
                element.current.onmouseover = togglePopover;
                element.current.onmouseout = closePopover;
            }
            if (finalTrigger === Triggers.CLICK) {
                element.current.onclick = togglePopover;
            }
        }
    }, [element, finalTrigger, state.open]);
    useEffect(() => {
        if (isMobile) {
            setFinalTrigger("click");
        }
        else {
            setFinalTrigger(trigger);
        }
    }, [trigger, isMobile]);
    useEffect(() => {
        dispatch({ type: "setIsOpen", payload: setIsOpen });
    }, [setIsOpen]);
    useLayoutEffect(() => {
        const d = getFirstElementByClassName(popoverId);
        let resizeObserver;
        if (d) {
            resizeObserver = new ResizeObserver(checkForOpen);
            resizeObserver.observe(d);
        }
        return () => resizeObserver?.disconnect();
    }, [popoverId, state.open]);
    return (React.createElement(Popover, { containerClassName: clsx(popoverClassName, popoverId), isOpen: state.open, align: align, positions: [location], reposition: reposition, content: ({ position, childRect, popoverRect }) => (React.createElement(ArrowContainer, { position: position, childRect: childRect, popoverRect: popoverRect, arrowColor: arrowColor, arrowSize: showArrow ? 10 : 0, className: "bcr-popover-arrow-container", arrowClassName: "bcr-popover-arrow" }, children)), parentElement: portal, onClickOutside: (event) => {
            if (finalTrigger === Triggers.CLICK) {
                closePopover(event);
            }
        } },
        React.createElement(PopoverElementBacker, { height: elementHeight, width: elementWidth, id: popoverId })));
};
export default GenericPopover;
//# sourceMappingURL=GenericPopover.js.map